import { statusMapping, type ReportStatus } from '../constants';
import { fetchWithToken } from '../utils/fetchWithToken';

export interface ApiReport {
  id: string;
  case_reference_no: string;
  status: string;
  service: string;
  created_at: string;
  updated_at: string;
  service_data: {
    back_office_status: string;
    access_notes: string;
  };
}

export interface Report {
  id: string;
  title: string;
  caseReference: string;
  dateCreated: string;
  dateUpdated: string;
  status: ReportStatus;
}

interface GetUserCasesParams {
  id?: string;
  page?: number;
  page_size?: number;
  status?: string;
  created_from?: string;
  created_to?: string;
  sort_by?: string;
  sort_order?: string;
  service?: string;
}

interface ApiResponse {
  data: ApiReport[];
  pagination: {
    total_records: number;
    current_page: number;
    page_size: number;
    total_pages: number;
    next_page: number | null;
    prev_page: number | null;
  };
  meta: {
    sorted_by: string;
    sort_order: string;
  };
}

interface GetUserCasesResponse {
  cases: Report[];
  total: number;
  page: number;
  page_size: number;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-GB', { month: 'long' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const transformApiReport = (apiReport: ApiReport): Report => {
  return {
    id: apiReport.id,
    title: apiReport.service,
    caseReference: apiReport.case_reference_no,
    dateCreated: formatDate(apiReport.created_at),
    dateUpdated: formatDate(apiReport.updated_at),
    status: statusMapping[apiReport.status] || 'Received',
  };
};

export interface UserService {
  id: string;
  name: string;
  public_id: string;
}

export const getUserServices = async (): Promise<UserService[]> => {
  try {
    const url = `${import.meta.env.VITE_APIM_BASE_URL}/report/v1/case/user-services`;

    const response = await fetchWithToken(url, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user services: ${response.statusText}`);
    }

    const services: UserService[] = await response.json();
    return services;
  } catch (error) {
    console.error('Error fetching user services:', error);
    throw error;
  }
};

export const getUserCases = async (
  params?: GetUserCasesParams,
): Promise<GetUserCasesResponse> => {
  try {
    const queryParams = new URLSearchParams();

    if (params?.id) queryParams.append('id', params.id);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size)
      queryParams.append('page_size', params.page_size.toString());
    if (params?.status)
      queryParams.append('status', params?.status?.replaceAll(' ', ''));
    if (params?.created_from)
      queryParams.append('created_from', params.created_from);
    if (params?.created_to) queryParams.append('created_to', params.created_to);
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);
    if (params?.service) queryParams.append('service', params.service);

    const queryString = queryParams.toString();
    const url = `${import.meta.env.VITE_APIM_BASE_URL}/report/v1/case/user-cases${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithToken(url, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user cases: ${response.statusText}`);
    }

    const apiResponse: ApiResponse = await response.json();

    return {
      cases: apiResponse.data?.map(transformApiReport) || [],
      total: apiResponse.pagination?.total_records || 0,
      page: apiResponse.pagination?.current_page || 1,
      page_size: apiResponse.pagination?.page_size || 10,
    };
  } catch (error) {
    console.error('Error fetching user cases:', error);
    throw error;
  }
};

export interface UserStatus {
  id: string;
  name: string;
}

export const getUserStatuses = async (): Promise<UserStatus[]> => {
  try {
    const response = await fetchWithToken(
      `${import.meta.env.VITE_APIM_BASE_URL}/report/v1/case/user-statuses`,
      {
        method: 'GET',
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch user statuses: ${response.statusText}`);
    }

    const statuses: UserStatus[] = await response.json();
    return statuses;
  } catch (error) {
    console.error('Error fetching user statuses:', error);
    throw error;
  }
};
